import React, { useState } from "react";
import styled from "styled-components";
import { Button, PasswordInput } from "components";
import { ModalWindow } from "components/ModalWindow";
import { getTokenDisplayName } from "../../constants/token";
import { useScreen } from "hooks";
import {
    getTokenAmountWithFee,
    isPositiveTokenAmount,
} from "utils/balanceUtils";

const Title = styled.div`
    color: ${({ theme }) => theme.text.primary};
    margin-bottom: 16px;
    text-align: center;
`;

const Description = styled.p`
    font-size: 14px;
    color: ${({ theme }) => theme.text.secondary};
    margin-bottom: 24px;
    text-align: center;
`;

const TransactionDetails = styled.div`
    background: ${({ theme }) => theme.surface};
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
    border: 1px solid ${({ theme }) => theme.border};
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid ${({ theme }) => theme.border};

    &:last-child {
        border-bottom: none;
    }
`;

const DetailLabel = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme.text.secondary};
    font-weight: 500;
`;

const DetailValue = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme.text.primary};
    font-weight: 600;
    word-break: break-all;
    text-align: right;
    max-width: 60%;
`;

const AmountValue = styled(DetailValue)`
    color: ${({ theme }) => theme.primary};
    font-size: 16px;
    font-weight: 700;
`;

const AddressValue = styled(DetailValue)`
    font-size: 12px;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 24px;

    @media (max-width: 768px) {
        display: block;
        padding: 0 2rem;
    }
`;

const WarningText = styled.div`
    background: ${({ theme }) => `${theme.warning}20`};
    color: ${({ theme }) => theme.warning};
    padding: 12px;
    border-radius: 8px;
    font-size: 14px;
    margin-bottom: 20px;
    text-align: center;
    border: 1px solid ${({ theme }) => `${theme.warning}40`};
`;

const AdaptiveButton = styled(Button)`
    padding-left: 29px;
    padding-right: 29px;
    text-wrap: nowrap;
`;

interface TransactionConfirmationProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (password?: string) => void;
    amount: string;
    recipient: string;
    senderAddress: string;
    senderName: string;
    maxFee: bigint;
    feeLabel: string;
    feeDetailLabel?: string;
    totalLabel?: string;
    loading?: boolean;
    needsPassword?: boolean;
}

export const TransactionConfirmationModal: React.FC<
    TransactionConfirmationProps
> = ({
    isOpen,
    onClose,
    onConfirm,
    amount,
    recipient,
    senderAddress,
    senderName,
    maxFee,
    feeLabel,
    feeDetailLabel = "Commission amount can be :",
    totalLabel = "Total Cost:",
    loading = false,
    needsPassword = false,
}) => {
    const { isLaptop } = useScreen();

    const [password, setPassword] = useState("");

    const totalAmount = isPositiveTokenAmount(amount)
        ? (getTokenAmountWithFee(amount, maxFee) ?? "—")
        : "—";

    const handleClose = () => {
        if (!loading) {
            setPassword("");
            onClose();
        }
    };

    const handleConfirm = () => {
        if (needsPassword && !password.trim()) {
            return;
        }
        onConfirm(needsPassword ? password : undefined);
    };

    return (
        <ModalWindow
            isOpen={isOpen}
            onClose={handleClose}
            maxWidth="500px"
            dismissible={!loading}
            aria-label="Confirm transaction"
        >
            <Title>
                <h1>Confirm Transaction</h1>
            </Title>
            <Description>
                Please review the transaction details before sending.
            </Description>

            <WarningText>
                WARNING: This transaction cannot be reversed once sent. Please
                verify all details carefully.
            </WarningText>

            <TransactionDetails>
                <DetailRow>
                    <DetailLabel>From Account:</DetailLabel>
                    <DetailValue>{senderName}</DetailValue>
                </DetailRow>

                <DetailRow>
                    <DetailLabel>From Address:</DetailLabel>
                    <AddressValue>{senderAddress}</AddressValue>
                </DetailRow>

                <DetailRow>
                    <DetailLabel>To Address:</DetailLabel>
                    <AddressValue>{recipient}</AddressValue>
                </DetailRow>

                <DetailRow>
                    <DetailLabel>Amount:</DetailLabel>
                    <AmountValue>
                        {amount} {getTokenDisplayName()}
                    </AmountValue>
                </DetailRow>

                <DetailRow>
                    <DetailLabel>{feeDetailLabel}</DetailLabel>
                    <DetailValue>
                        {feeLabel} {getTokenDisplayName()}
                    </DetailValue>
                </DetailRow>

                <DetailRow>
                    <DetailLabel>{totalLabel}</DetailLabel>
                    <AmountValue>
                        {totalAmount} {getTokenDisplayName()}
                    </AmountValue>
                </DetailRow>
            </TransactionDetails>

            {needsPassword && (
                <div style={{ marginBottom: "24px" }}>
                    <PasswordInput
                        id="transaction-confirmation-password-input"
                        data-testid="transaction-confirmation-password-input"
                        data-cy="transaction-confirmation-password-input"
                        label="Account Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onInput={(e) => {
                            const target = e.currentTarget;
                            if (target.value !== password) {
                                setPassword(target.value);
                            }
                        }}
                        placeholder="Enter password"
                        wrapperStyle={{ marginBottom: "16px" }}
                        autoComplete="current-password"
                        autoFocus={needsPassword}
                    />
                </div>
            )}

            <Actions>
                <AdaptiveButton
                    onClick={handleConfirm}
                    loading={loading}
                    disabled={needsPassword && !password.trim()}
                    fullWidth={isLaptop}
                    style={{
                        ...(isLaptop && {
                            marginBottom: "12px",
                        }),
                    }}
                >
                    Confirm & Send
                </AdaptiveButton>
                <AdaptiveButton
                    variant="secondary"
                    onClick={handleClose}
                    disabled={loading}
                    fullWidth={isLaptop}
                >
                    Cancel
                </AdaptiveButton>
            </Actions>
        </ModalWindow>
    );
};
